import { useState } from 'react'
import type { ReactElement, ReactNode } from 'react'

import { exclamationsFor, heatLevel, heatValue } from '@wasp/heat'

import { useSession } from '../../auth/useSession'
import { Button, ErrorState, InputText, ProductGroupPicker, Select, Spinner } from '../../components'
import { trpcMessage } from '../../lib/trpcMessages'
import { trpc } from '../../trpc'
import type { RouterInputs, RouterOutputs } from '../../trpc'
import { AdminPageHeader, ReadOnlyNote, adminStyles as admin, changedFields, hasFields, numberText } from '../admin/AdminPage'
import { RacionadoPreview } from './RacionadoPreview'
import type { PreviewCard } from './RacionadoPreview'
import styles from './RacionadoSettingsPage.module.scss'
import { ThresholdRuler, markValue } from './ThresholdRuler'
import type { MarkTexts } from './ThresholdRuler'

type SettingsView = RouterOutputs['configuration']['racionadoSettings']['get']
type Settings = SettingsView['settings']
// The interchangeable chiller types decide nothing any more (R6, R7): the page neither shows nor saves them.
type Edited = Omit<Settings, 'interchangeableChillTypeIds'>
type Patch = RouterInputs['configuration']['racionadoSettings']['update']
type Marks = [number, number, number]

type Draft = {
  toChill: { targetMin: string; heatMin: MarkTexts; exclamationsMin: MarkTexts }
  toTunnel: { targetMin: string; exclamationsMin: MarkTexts }
  overChill: { exclamationsMin: MarkTexts }
  missingExit: { exclamationsMin: MarkTexts }
  toTupper: { exclamationsH: MarkTexts }
  unused: { bucketsH: MarkTexts; exclamationsH: MarkTexts }
  chillMinutesByType: Record<string, string>
  temperatures: { minIn: string; maxOut: string }
  departments: { chill: string; hot: string; cold: string; seal: string }
  types: { chillTypeIds: string; tunnelTypeIds: string; prepTypeIds: string; labelTypeIds: string }
  carts: Array<{ id: string; name: string; baseG: string; perPackG: string; packLabel: PackLabel }>
  storageGroupIds: Array<number>
  /** The hot board's slot groups, in the board's order. */
  stationGroupIds: Array<string>
  mrpStationMarginMin: string
  expiryWarningH: string
  /** The blue box: its maximum and its three «!», as typed. */
  blueBox: { maxMin: string; exclamationsMin: MarkTexts }
}

const marksText = (marks: Marks): MarkTexts => [String(marks[0]), String(marks[1]), String(marks[2])]
const idsText = (ids: Array<number>): string => ids.join(', ')

const draftOf = (settings: Settings): Draft => ({
  toChill: { targetMin: String(settings.toChill.targetMin), heatMin: marksText(settings.toChill.heatMin), exclamationsMin: marksText(settings.toChill.exclamationsMin) },
  toTunnel: { targetMin: String(settings.toTunnel.targetMin), exclamationsMin: marksText(settings.toTunnel.exclamationsMin) },
  overChill: { exclamationsMin: marksText(settings.overChill.exclamationsMin) },
  missingExit: { exclamationsMin: marksText(settings.missingExit.exclamationsMin) },
  toTupper: { exclamationsH: marksText(settings.toTupper.exclamationsH) },
  unused: { bucketsH: marksText(settings.unused.bucketsH), exclamationsH: marksText(settings.unused.exclamationsH) },
  chillMinutesByType: Object.fromEntries(Object.entries(settings.chillMinutesByType).map(([typeId, minutes]) => [typeId, String(minutes)])),
  temperatures: { minIn: numberText(settings.temperatures.minIn), maxOut: numberText(settings.temperatures.maxOut) },
  departments: {
    chill: String(settings.departments.chill),
    hot: String(settings.departments.hot),
    cold: String(settings.departments.cold),
    seal: String(settings.departments.seal),
  },
  types: {
    chillTypeIds: idsText(settings.chillTypeIds),
    tunnelTypeIds: idsText(settings.tunnelTypeIds),
    prepTypeIds: idsText(settings.prepTypeIds),
    labelTypeIds: idsText(settings.labelTypeIds),
  },
  carts: settings.carts.map((cart) => ({ id: cart.id, name: cart.name, baseG: String(cart.baseG), perPackG: String(cart.perPackG), packLabel: cart.packLabel })),
  storageGroupIds: settings.storageGroupIds,
  stationGroupIds: settings.stationGroupIds,
  mrpStationMarginMin: String(settings.mrpStationMarginMin),
  expiryWarningH: String(settings.expiryWarningH),
  blueBox: { maxMin: String(settings.blueBox.maxMin), exclamationsMin: marksText(settings.blueBox.exclamationsMin) },
})

const positive = (text: string): number | null => {
  const value = markValue(text)
  return value !== null && value > 0 ? value : null
}

const SIGNED_DECIMAL = /^-?\d+(,\d+)?$/

const celsius = (text: string): number | null =>
  SIGNED_DECIMAL.test(text.trim()) ? Number(text.trim().replace(',', '.')) : null

/** The three marks, or null while one is not a whole number. Their order is checked apart. */
export const marksOf = (texts: MarkTexts): Marks | null => {
  const [first, second, third] = texts.map(markValue)
  return first === null || second === null || third === null ? null : [first, second, third]
}

const ascending = ([first, second, third]: Marks): boolean => first <= second && second <= third

const idsOf = (text: string): Array<number> | null => {
  const parts = text.split(/[;,\s]+/).filter((part) => part !== '')
  const ids = parts.map(positive)
  return ids.every((id): id is number => id !== null) ? ids : null
}

type Parsed = { next: Edited | null; problems: Array<string> }

/**
 * The settings the draft makes, or null with the names of what does not
 * parse or is out of order: nothing is saved half-read.
 */
const parseDraft = (draft: Draft): Parsed => {
  const problems: Array<string> = []
  const need = <T,>(value: T | null, label: string, fallback: T): T => {
    if (value === null) {
      problems.push(label)
      return fallback
    }
    return value
  }
  const marks = (texts: MarkTexts, label: string): Marks => {
    const value = need<Marks>(marksOf(texts), label, [0, 0, 0])
    if (!ascending(value)) {
      problems.push(`${label} (de menor a mayor)`)
    }
    return value
  }
  const next: Edited = {
    toChill: {
      targetMin: need(positive(draft.toChill.targetMin), 'Para abatir: objetivo', 1),
      heatMin: marks(draft.toChill.heatMin, 'Para abatir: calor'),
      exclamationsMin: marks(draft.toChill.exclamationsMin, 'Para abatir: avisos'),
    },
    toTunnel: {
      targetMin: need(positive(draft.toTunnel.targetMin), 'Para el túnel: objetivo', 1),
      exclamationsMin: marks(draft.toTunnel.exclamationsMin, 'Para el túnel: avisos'),
    },
    overChill: { exclamationsMin: marks(draft.overChill.exclamationsMin, 'Listo, sácalo') },
    missingExit: { exclamationsMin: marks(draft.missingExit.exclamationsMin, 'Faltan datos') },
    toTupper: { exclamationsH: marks(draft.toTupper.exclamationsH, 'Semis a tuppers') },
    unused: {
      bucketsH: marks(draft.unused.bucketsH, 'Franjas de la cámara'),
      exclamationsH: marks(draft.unused.exclamationsH, 'Semis sin usar'),
    },
    chillMinutesByType: Object.fromEntries(Object.entries(draft.chillMinutesByType)
      .map(([typeId, minutes]) => [typeId, need(positive(minutes), `Tiempo de abatir del tipo ${typeId}`, 1)])),
    temperatures: {
      minIn: need(celsius(draft.temperatures.minIn), 'Temperatura de entrada', 0),
      maxOut: need(celsius(draft.temperatures.maxOut), 'Temperatura de salida', 0),
    },
    departments: {
      chill: need(positive(draft.departments.chill), 'Departamento de abatimiento', 1),
      hot: need(positive(draft.departments.hot), 'Departamento de caliente', 1),
      cold: need(positive(draft.departments.cold), 'Departamento de frío', 1),
      seal: need(positive(draft.departments.seal), 'Departamento de termosellado', 1),
    },
    chillTypeIds: need(idsOf(draft.types.chillTypeIds), 'Tipos de abatidor', []),
    tunnelTypeIds: need(idsOf(draft.types.tunnelTypeIds), 'Tipos de túnel', []),
    prepTypeIds: need(idsOf(draft.types.prepTypeIds), 'Tipos de preparar en frío', []),
    labelTypeIds: need(idsOf(draft.types.labelTypeIds), 'Tipos de etiquetar', []),
    carts: draft.carts.map((cart) => ({
      id: cart.id,
      name: need(cart.name.trim() === '' ? null : cart.name.trim(), 'Nombre de un carro', ''),
      baseG: need(markValue(cart.baseG), `Peso de ${cart.name || 'un carro'}`, 0),
      perPackG: need(markValue(cart.perPackG), `Peso por ${packOf(cart.packLabel)} de ${cart.name || 'un carro'}`, 0),
      packLabel: cart.packLabel,
    })),
    storageGroupIds: draft.storageGroupIds,
    stationGroupIds: draft.stationGroupIds,
    mrpStationMarginMin: need(markValue(draft.mrpStationMarginMin), 'Margen para la máquina de MRPeasy', 0),
    expiryWarningH: need(markValue(draft.expiryWarningH), 'Aviso de caducidad', 0),
    blueBox: {
      maxMin: need(positive(draft.blueBox.maxMin), 'Caja azul: tope', 1),
      exclamationsMin: marks(draft.blueBox.exclamationsMin, 'Caja azul: avisos'),
    },
  }
  return { next: problems.length === 0 ? next : null, problems }
}

const same = (left: unknown, right: unknown): boolean => JSON.stringify(left) === JSON.stringify(right)

/**
 * What saving sends: within each block only the keys that changed, so
 * editing the target sends `{ toChill: { targetMin: 25 } }`; the lists, the
 * carts and the minutes by type, whole.
 */
const patchOf = (settings: Settings, next: Edited): Patch => {
  const patch: Patch = {}
  const toChill = changedFields(settings.toChill, next.toChill)
  if (hasFields(toChill)) {
    patch.toChill = toChill
  }
  const toTunnel = changedFields(settings.toTunnel, next.toTunnel)
  if (hasFields(toTunnel)) {
    patch.toTunnel = toTunnel
  }
  const blueBox = changedFields(settings.blueBox, next.blueBox)
  if (hasFields(blueBox)) {
    patch.blueBox = blueBox
  }
  const overChill = changedFields(settings.overChill, next.overChill)
  if (hasFields(overChill)) {
    patch.overChill = overChill
  }
  const missingExit = changedFields(settings.missingExit, next.missingExit)
  if (hasFields(missingExit)) {
    patch.missingExit = missingExit
  }
  const toTupper = changedFields(settings.toTupper, next.toTupper)
  if (hasFields(toTupper)) {
    patch.toTupper = toTupper
  }
  const unused = changedFields(settings.unused, next.unused)
  if (hasFields(unused)) {
    patch.unused = unused
  }
  const temperatures = changedFields(settings.temperatures, next.temperatures)
  if (hasFields(temperatures)) {
    patch.temperatures = temperatures
  }
  const departments = changedFields(settings.departments, next.departments)
  if (hasFields(departments)) {
    patch.departments = departments
  }
  if (!same(settings.chillMinutesByType, next.chillMinutesByType)) {
    patch.chillMinutesByType = next.chillMinutesByType
  }
  if (!same(settings.chillTypeIds, next.chillTypeIds)) {
    patch.chillTypeIds = next.chillTypeIds
  }
  if (!same(settings.tunnelTypeIds, next.tunnelTypeIds)) {
    patch.tunnelTypeIds = next.tunnelTypeIds
  }
  if (!same(settings.prepTypeIds, next.prepTypeIds)) {
    patch.prepTypeIds = next.prepTypeIds
  }
  if (!same(settings.labelTypeIds, next.labelTypeIds)) {
    patch.labelTypeIds = next.labelTypeIds
  }
  if (!same(settings.carts, next.carts)) {
    patch.carts = next.carts
  }
  if (!same(settings.storageGroupIds, next.storageGroupIds)) {
    patch.storageGroupIds = next.storageGroupIds
  }
  if (!same(settings.stationGroupIds, next.stationGroupIds)) {
    patch.stationGroupIds = next.stationGroupIds
  }
  if (settings.mrpStationMarginMin !== next.mrpStationMarginMin) {
    patch.mrpStationMarginMin = next.mrpStationMarginMin
  }
  if (settings.expiryWarningH !== next.expiryWarningH) {
    patch.expiryWarningH = next.expiryWarningH
  }
  return patch
}

/** The board blocks with «Volver a los valores por defecto», and the keys each one restores. */
const BLOCK_KEYS = {
  caliente: ['toChill', 'toTunnel'],
  cajaAzul: ['blueBox'],
  frio: ['overChill', 'missingExit', 'toTupper', 'chillMinutesByType'],
  camara: ['unused'],
} as const satisfies Record<string, ReadonlyArray<keyof Settings & keyof Draft>>

type BlockId = keyof typeof BLOCK_KEYS

const defaultsPatch = (block: BlockId, defaults: Settings): Patch => {
  if (block === 'caliente') {
    return { toChill: defaults.toChill, toTunnel: defaults.toTunnel }
  }
  if (block === 'cajaAzul') {
    return { blueBox: defaults.blueBox }
  }
  if (block === 'frio') {
    return { overChill: defaults.overChill, missingExit: defaults.missingExit, toTupper: defaults.toTupper, chillMinutesByType: defaults.chillMinutesByType }
  }
  return { unused: defaults.unused }
}

const copyKey = <K extends keyof Draft>(into: Partial<Draft>, from: Draft, key: K): void => {
  into[key] = from[key]
}

const atDefaults = (block: BlockId, draft: Draft, settings: Settings, defaults: Settings): boolean => {
  const defaultDraft = draftOf(defaults)
  return BLOCK_KEYS[block].every((key) => same(draft[key], defaultDraft[key]) && same(settings[key], defaults[key]))
}

/** The hot board measures the heat by the slack to the target, with the marks as `[cold, mild, hot, burning]` (`@wasp/racionado`). */
const hotCard = (draft: Draft) => (waitingMin: number): PreviewCard | null => {
  const targetMin = positive(draft.toChill.targetMin)
  const heat = marksOf(draft.toChill.heatMin)
  const exclamations = marksOf(draft.toChill.exclamationsMin)
  if (targetMin === null || heat === null || exclamations === null) {
    return null
  }
  const slack = targetMin - waitingMin
  const thresholds = [heat[2], heat[1], heat[0], 0] as const
  return {
    title: 'Salsa de tomate · MO84309',
    detail: `Salió de cocina hace ${waitingMin} min · objetivo, ${targetMin} min`,
    exclamations: exclamationsFor(waitingMin, exclamations),
    heat: { value: Math.round(heatValue(slack, thresholds)), level: heatLevel(slack, thresholds) },
    late: waitingMin > targetMin,
  }
}

const coldCard = (draft: Draft) => (overMin: number): PreviewCard | null => {
  const exclamations = marksOf(draft.overChill.exclamationsMin)
  if (exclamations === null) {
    return null
  }
  return {
    title: 'Guarnición de arroz · Hiber 2',
    detail: overMin === 0 ? 'Listo ahora: sácalo' : `Listo hace ${overMin} min: sácalo`,
    exclamations: exclamationsFor(overMin, exclamations),
  }
}

/** The cold room's bands, by their marks: «menos de 2 h», «de 2 a 8 h»… */
const bandLabels = ([first, second, third]: Marks): [string, string, string, string] =>
  [`menos de ${first} h`, `de ${first} a ${second} h`, `de ${second} a ${third} h`, `más de ${third} h`]

const roomCard = (draft: Draft) => (ageH: number): PreviewCard | null => {
  const bands = marksOf(draft.unused.bucketsH)
  const exclamations = marksOf(draft.unused.exclamationsH)
  if (bands === null || exclamations === null) {
    return null
  }
  return {
    title: 'Pollo asado · lote de 26.800 g',
    detail: `Salió del abatidor hace ${ageH} h y no se ha gastado`,
    exclamations: exclamationsFor(ageH, exclamations),
    band: bandLabels(bands)[exclamationsFor(ageH, bands)],
  }
}

type InlineProps = { label: string; value: string; onChange: (value: string) => void; disabled: boolean; wide?: boolean }

/**
 * A number inside a sentence: a native input, because the kit's InputText is a
 * labelled block and here the sentence is the label. Its name says it whole.
 */
const Inline = ({ label, value, onChange, disabled, wide = false }: InlineProps): ReactElement => (
  <input
    className={`${styles.inline} ${wide ? styles['inline--wide'] : ''}`}
    inputMode="decimal"
    value={value}
    aria-label={label}
    disabled={disabled}
    onChange={(event) => onChange(event.target.value)}
  />
)

type BlockProps = {
  title: string
  intro: string
  restore?: { disabled: boolean; onClick: () => void }
  preview?: ReactNode
  children: ReactNode
}

const Block = ({ title, intro, restore, preview, children }: BlockProps): ReactElement => (
  <section className={styles.block} aria-label={title}>
    <header className={styles.block__head}>
      <div>
        <h2 className={styles.block__title}>{title}</h2>
        <p className={styles.block__intro}>{intro}</p>
      </div>
      {restore === undefined ? null : <Button variant="secondary" disabled={restore.disabled} onClick={restore.onClick} label="Volver a los valores por defecto" />}
    </header>
    <div className={styles.block__body}>
      <div className={styles.block__alarms}>{children}</div>
      {preview}
    </div>
  </section>
)

const Alarm = ({ title, children }: { title: string; children: ReactNode }): ReactElement => (
  <div className={styles.alarm}>
    <h3 className={styles.alarm__title}>{title}</h3>
    {children}
  </div>
)

type PackLabel = 'bandejas' | 'envases'

// What goes on a cart, as the cold board's exit form names it: trays on an oven cart (its gastros), containers on the others.
const PACK_OPTIONS = [{ value: 'bandejas', label: 'Bandejas' }, { value: 'envases', label: 'Envases' }]
const packLabelOf = (value: string): PackLabel => (value === 'bandejas' ? 'bandejas' : 'envases')
const packOf = (label: PackLabel): string => (label === 'bandejas' ? 'bandeja' : 'envase')

const nextCartId = (carts: Draft['carts']): string => {
  const taken = new Set(carts.map((cart) => cart.id))
  let index = carts.length + 1
  while (taken.has(`carro-${index}`)) {
    index += 1
  }
  return `carro-${index}`
}

const SettingsForm = ({ view }: { view: SettingsView }): ReactElement => {
  const { isReadOnly } = useSession()
  const utils = trpc.useUtils()
  const { settings, defaults } = view
  const [draft, setDraft] = useState<Draft>(() => draftOf(settings))
  const [errorMessage, setErrorMessage] = useState<string | null>(null)
  const [saved, setSaved] = useState(false)

  const update = trpc.configuration.racionadoSettings.update.useMutation({
    onSuccess: () => {
      setSaved(true)
      void utils.configuration.racionadoSettings.get.invalidate()
    },
    onError: (error) => setErrorMessage(trpcMessage(error)),
  })

  const { next, problems } = parseDraft(draft)
  const patch = next === null ? {} : patchOf(settings, next)
  const changed = hasFields(patch)
  const canSave = !isReadOnly && next !== null && changed && !update.isPending

  const edit = (change: Partial<Draft>): void => {
    setDraft({ ...draft, ...change })
    setSaved(false)
    setErrorMessage(null)
  }

  const restore = (block: BlockId): void => {
    const defaultDraft = draftOf(defaults)
    const reset: Partial<Draft> = {}
    for (const key of BLOCK_KEYS[block]) {
      copyKey(reset, defaultDraft, key)
    }
    edit(reset)
    if (!BLOCK_KEYS[block].every((key) => same(settings[key], defaults[key]))) {
      update.mutate(defaultsPatch(block, defaults))
    }
  }

  const toggleGroup = (groupId: string): void =>
    edit({
      stationGroupIds: draft.stationGroupIds.includes(groupId)
        ? draft.stationGroupIds.filter((id) => id !== groupId)
        : [...draft.stationGroupIds, groupId],
    })

  // The chosen groups first, in the board's order, then the rest. A chosen
  // group that is no longer active is still listed, so it can be unticked.
  const nameOf = new Map(view.stationGroups.map((group) => [group.id, group.name]))
  const orderedGroups = [
    ...draft.stationGroupIds.map((id) => ({ id, name: nameOf.get(id) ?? 'Grupo inactivo o desconocido' })),
    ...view.stationGroups.filter((group) => !draft.stationGroupIds.includes(group.id)),
  ]

  const restoreOf = (block: BlockId) => ({ disabled: isReadOnly || update.isPending || atDefaults(block, draft, settings, defaults), onClick: () => restore(block) })
  const typeName = (typeId: string): string => (settings.tunnelTypeIds.includes(Number(typeId)) ? `el túnel (tipo ${typeId})` : `un abatidor de tipo ${typeId}`)

  return (
    <form
      className={admin['admin-page__form']}
      noValidate
      onSubmit={(event) => {
        event.preventDefault()
        if (canSave) {
          update.mutate(patch)
        }
      }}
    >
      {isReadOnly ? <ReadOnlyNote /> : null}

      <Block
        title="Caliente"
        intro="Lo que sale de cocina entra en un abatidor lo antes posible: el tablero de caliente lo pinta más caliente cuanto más espera."
        restore={restoreOf('caliente')}
        preview={<RacionadoPreview board="caliente" caption="Así se ve un semi que salió de cocina hace" unit="min" initialSample={35} cardFor={hotCard(draft)} />}
      >
        <Alarm title="Para abatir">
          <p className={styles.sentence}>
            Avisar si un semi tarda más de{' '}
            <Inline label="Para abatir: objetivo (min)" value={draft.toChill.targetMin} disabled={isReadOnly} onChange={(targetMin) => edit({ toChill: { ...draft.toChill, targetMin } })} />
            {' '}min en entrar al abatidor desde que acaba su cocción.
          </p>
          <p className={styles.sentence}>Se calienta según los minutos que le faltan para ese objetivo:</p>
          <ThresholdRuler
            name="Para abatir: calor"
            unit="min"
            marks={['ardiente', 'caliente', 'templado']}
            values={draft.toChill.heatMin}
            scale={60}
            urgentFirst
            disabled={isReadOnly}
            onChange={(heatMin) => edit({ toChill: { ...draft.toChill, heatMin } })}
          />
          <p className={styles.sentence}>Y las «!» según los minutos que lleva esperando:</p>
          <ThresholdRuler
            name="Para abatir"
            unit="min"
            marks={['!', '!!', '!!!']}
            values={draft.toChill.exclamationsMin}
            scale={90}
            disabled={isReadOnly}
            onChange={(exclamationsMin) => edit({ toChill: { ...draft.toChill, exclamationsMin } })}
          />
        </Alarm>
        <Alarm title="Para el túnel">
          <p className={styles.sentence}>
            Avisar si un racionado al túnel tarda más de{' '}
            <Inline label="Para el túnel: objetivo (min)" value={draft.toTunnel.targetMin} disabled={isReadOnly} onChange={(targetMin) => edit({ toTunnel: { ...draft.toTunnel, targetMin } })} />
            {' '}min en empezar el dosificador desde la última cocción.
          </p>
          <ThresholdRuler
            name="Para el túnel"
            unit="min"
            marks={['!', '!!', '!!!']}
            values={draft.toTunnel.exclamationsMin}
            scale={60}
            disabled={isReadOnly}
            onChange={(exclamationsMin) => edit({ toTunnel: { ...draft.toTunnel, exclamationsMin } })}
          />
        </Alarm>
      </Block>

      <Block
        title="Caja azul"
        intro="Lo que espera en caliente puede entrar en la caja azul, que mantiene su temperatura: su calor no corre dentro, y su reloj de la caja cuenta desde la primera vez que entró."
        restore={restoreOf('cajaAzul')}
      >
        <Alarm title="Tiempo en la caja">
          <p className={styles.sentence}>
            Avisar de una tarea que lleva más de{' '}
            <Inline label="Caja azul: tope (min)" value={draft.blueBox.maxMin} disabled={isReadOnly} onChange={(maxMin) => edit({ blueBox: { ...draft.blueBox, maxMin } })} />
            {' '}min desde que entró en la caja azul. Solo avisa: no bloquea nada.
          </p>
          <p className={styles.sentence}>Y las «!» según los minutos desde que entró:</p>
          <ThresholdRuler
            name="Caja azul"
            unit="min"
            marks={['!', '!!', '!!!']}
            values={draft.blueBox.exclamationsMin}
            scale={150}
            disabled={isReadOnly}
            onChange={(exclamationsMin) => edit({ blueBox: { ...draft.blueBox, exclamationsMin } })}
          />
        </Alarm>
      </Block>

      <Block
        title="Frío"
        intro="Frío saca lo abatido, informa la salida y cierra los tuppers."
        restore={restoreOf('frio')}
        preview={<RacionadoPreview board="frío" caption="Así se ve un lote que lleva de más en el abatidor" unit="min" initialSample={20} cardFor={coldCard(draft)} />}
      >
        <Alarm title="Tiempo de abatir">
          {Object.keys(draft.chillMinutesByType).map((typeId) => (
            <p key={typeId} className={styles.sentence}>
              {`En ${typeName(typeId)}, un lote está listo a los `}
              <Inline
                label={`Tiempo de abatir del tipo ${typeId} (min)`}
                value={draft.chillMinutesByType[typeId] ?? ''}
                disabled={isReadOnly}
                onChange={(minutes) => edit({ chillMinutesByType: { ...draft.chillMinutesByType, [typeId]: minutes } })}
              />
              {' '}min.
            </p>
          ))}
        </Alarm>
        <Alarm title="Listo, sácalo">
          <p className={styles.sentence}>Avisar de un lote listo que sigue en el abatidor, por los minutos de más:</p>
          <ThresholdRuler
            name="Listo, sácalo"
            unit="min"
            marks={['!', '!!', '!!!']}
            values={draft.overChill.exclamationsMin}
            scale={45}
            disabled={isReadOnly}
            onChange={(exclamationsMin) => edit({ overChill: { exclamationsMin } })}
          />
        </Alarm>
        <Alarm title="Faltan datos">
          <p className={styles.sentence}>Avisar de un lote terminado que sigue sin sus datos de salida, por los minutos desde que se terminó:</p>
          <ThresholdRuler
            name="Faltan datos"
            unit="min"
            marks={['!', '!!', '!!!']}
            values={draft.missingExit.exclamationsMin}
            scale={90}
            disabled={isReadOnly}
            onChange={(exclamationsMin) => edit({ missingExit: { exclamationsMin } })}
          />
        </Alarm>
        <Alarm title="Semis a tuppers">
          <p className={styles.sentence}>Avisar de un tupper sin sellar, por las horas desde que tiene su primera parte:</p>
          <ThresholdRuler
            name="Semis a tuppers"
            unit="h"
            marks={['!', '!!', '!!!']}
            values={draft.toTupper.exclamationsH}
            scale={36}
            disabled={isReadOnly}
            onChange={(exclamationsH) => edit({ toTupper: { exclamationsH } })}
          />
        </Alarm>
      </Block>

      <Block
        title="Cámara"
        intro="Lo abatido con su salida informada y algo por gastar, por antigüedad desde que se informó la salida."
        restore={restoreOf('camara')}
        preview={<RacionadoPreview board="cámara" caption="Así se ve un semi que salió del abatidor hace" unit="h" initialSample={10} cardFor={roomCard(draft)} />}
      >
        <Alarm title="Franjas de la cámara">
          <p className={styles.sentence}>La cámara agrupa los semis por las horas desde su salida:</p>
          <ThresholdRuler
            name="Franjas de la cámara"
            unit="h"
            marks={['1.ª', '2.ª', '3.ª']}
            values={draft.unused.bucketsH}
            scale={36}
            disabled={isReadOnly}
            onChange={(bucketsH) => edit({ unused: { ...draft.unused, bucketsH } })}
          />
        </Alarm>
        <Alarm title="Semis sin usar">
          <p className={styles.sentence}>Avisar de un semi que sigue sin gastarse, por las horas desde su salida:</p>
          <ThresholdRuler
            name="Semis sin usar"
            unit="h"
            marks={['!', '!!', '!!!']}
            values={draft.unused.exclamationsH}
            scale={72}
            disabled={isReadOnly}
            onChange={(exclamationsH) => edit({ unused: { ...draft.unused, exclamationsH } })}
          />
        </Alarm>
      </Block>

      <fieldset className={admin['admin-page__fieldset']}>
        <legend className={admin['admin-page__legend']}>Grupos del tablero de caliente</legend>
        <p className={admin['admin-page__note']}>
          Los grupos de estación en que el tablero de caliente reparte sus huecos, en este orden: cada hueco va en el grupo que tiene su máquina. Las máquinas de cada grupo se asignan en «Grupos de estación».
        </p>
        <ul className={admin['admin-page__checklist']}>
          {orderedGroups.map((group) => (
            <li key={group.id} className={admin['admin-page__check-option']}>
              <label className={admin['admin-page__check-text']}>
                <input
                  type="checkbox"
                  className={admin['admin-page__checkbox']}
                  checked={draft.stationGroupIds.includes(group.id)}
                  disabled={isReadOnly}
                  onChange={() => toggleGroup(group.id)}
                />
                {group.name}
              </label>
            </li>
          ))}
        </ul>
      </fieldset>

      <fieldset className={admin['admin-page__fieldset']}>
        <legend className={admin['admin-page__legend']}>Para almacén</legend>
        <p className={admin['admin-page__note']}>
          Lo abatido de estas familias sale de la cámara a «Para almacén» si ningún tupper lo tiene reservado, y un tupper que reserva un lote suyo no se queda esperándolo.
        </p>
        <ProductGroupPicker
          label="Familias que se guardan para después"
          selected={draft.storageGroupIds}
          disabled={isReadOnly}
          onChange={(storageGroupIds) => edit({ storageGroupIds })}
        />
      </fieldset>

      <fieldset className={admin['admin-page__fieldset']}>
        <legend className={admin['admin-page__legend']}>Caducidad</legend>
        <p className={styles.sentence}>
          Avisar «Caduca en N h» en racionado frío desde{' '}
          <Inline label="Aviso de caducidad (h)" value={draft.expiryWarningH} disabled={isReadOnly} onChange={(expiryWarningH) => edit({ expiryWarningH })} />
          {' '}h antes de la caducidad de un lote.
        </p>
        <p className={admin['admin-page__note']}>La caducidad es la del último lote que MRPeasy recibió de su MO. Un lote caducado ya no se usa en ningún tupper y solo admite «Merma».</p>
      </fieldset>

      <fieldset className={admin['admin-page__fieldset']}>
        <legend className={admin['admin-page__legend']}>Autoasignar</legend>
        <p className={styles.sentence}>
          Llevar un semi al hueco de la máquina en que MRPeasy lo planificó si se libera como mucho{' '}
          <Inline label="Margen para la máquina de MRPeasy (min)" value={draft.mrpStationMarginMin} disabled={isReadOnly} onChange={(mrpStationMarginMin) => edit({ mrpStationMarginMin })} />
          {' '}min después que el primer hueco libre.
        </p>
        <p className={admin['admin-page__note']}>Con 0, la máquina de MRPeasy solo desempata entre huecos que se liberan a la vez. Después del margen va al que antes se libera.</p>
      </fieldset>

      <details className={admin['admin-page__details']}>
        <summary className={admin['admin-page__summary']}>Avanzado</summary>
        <div className={styles.advanced}>
          <fieldset className={admin['admin-page__fieldset']}>
            <legend className={admin['admin-page__legend']}>Temperaturas</legend>
            <p className={styles.sentence}>
              Avisar si un semi entra al abatidor a menos de{' '}
              <Inline label="Temperatura mínima de entrada (°C)" value={draft.temperatures.minIn} disabled={isReadOnly} onChange={(minIn) => edit({ temperatures: { ...draft.temperatures, minIn } })} />
              {' '}°C, o sale a más de{' '}
              <Inline label="Temperatura máxima de salida (°C)" value={draft.temperatures.maxOut} disabled={isReadOnly} onChange={(maxOut) => edit({ temperatures: { ...draft.temperatures, maxOut } })} />
              {' '}°C. Avisa, no bloquea.
            </p>
          </fieldset>

          <fieldset className={admin['admin-page__fieldset']}>
            <legend className={admin['admin-page__legend']}>Carros</legend>
            <p className={admin['admin-page__note']}>Al informar la salida, el neto es el bruto menos el carro y sus bandejas o envases: el formulario los llama como diga cada carro.</p>
            {draft.carts.map((cart, index) => (
              <div key={cart.id} className={admin['admin-page__inline-form']}>
                <InputText
                  label={`Carro ${index + 1}`}
                  value={cart.name}
                  disabled={isReadOnly}
                  onChange={(event) => edit({ carts: draft.carts.map((other) => (other.id === cart.id ? { ...other, name: event.target.value } : other)) })}
                />
                <InputText
                  label={`Peso del carro (g) · ${cart.name}`}
                  inputMode="numeric"
                  value={cart.baseG}
                  disabled={isReadOnly}
                  onChange={(event) => edit({ carts: draft.carts.map((other) => (other.id === cart.id ? { ...other, baseG: event.target.value } : other)) })}
                />
                <Select
                  label={`Lleva · ${cart.name}`}
                  options={PACK_OPTIONS}
                  value={cart.packLabel}
                  disabled={isReadOnly}
                  onChange={(event) => edit({ carts: draft.carts.map((other) => (other.id === cart.id ? { ...other, packLabel: packLabelOf(event.target.value) } : other)) })}
                />
                <InputText
                  label={`Por ${packOf(cart.packLabel)} (g) · ${cart.name}`}
                  inputMode="numeric"
                  value={cart.perPackG}
                  disabled={isReadOnly}
                  onChange={(event) => edit({ carts: draft.carts.map((other) => (other.id === cart.id ? { ...other, perPackG: event.target.value } : other)) })}
                />
                <Button
                  variant="secondary"
                  disabled={isReadOnly}
                  aria-label={`Quitar ${cart.name}`}
                  label="Quitar"
                  onClick={() => edit({ carts: draft.carts.filter((other) => other.id !== cart.id) })}
                />
              </div>
            ))}
            <div>
              <Button
                variant="secondary"
                disabled={isReadOnly}
                label="Añadir carro"
                onClick={() => edit({ carts: [...draft.carts, { id: nextCartId(draft.carts), name: '', baseG: '0', perPackG: '0', packLabel: 'envases' }] })}
              />
            </div>
          </fieldset>

          <fieldset className={admin['admin-page__fieldset']}>
            <legend className={admin['admin-page__legend']}>Departamentos</legend>
            <p className={admin['admin-page__note']}>El tablero de una operación es el departamento de su trabajador en MRPeasy, no su tipo.</p>
            <p className={styles.sentence}>
              Abatimiento{' '}
              <Inline label="Departamento de abatimiento" value={draft.departments.chill} disabled={isReadOnly} onChange={(chill) => edit({ departments: { ...draft.departments, chill } })} />
              {' '}· caliente{' '}
              <Inline label="Departamento de caliente" value={draft.departments.hot} disabled={isReadOnly} onChange={(hot) => edit({ departments: { ...draft.departments, hot } })} />
              {' '}· frío{' '}
              <Inline label="Departamento de frío" value={draft.departments.cold} disabled={isReadOnly} onChange={(cold) => edit({ departments: { ...draft.departments, cold } })} />
              {' '}· termosellado{' '}
              <Inline label="Departamento de termosellado" value={draft.departments.seal} disabled={isReadOnly} onChange={(seal) => edit({ departments: { ...draft.departments, seal } })} />
            </p>
          </fieldset>

          <fieldset className={admin['admin-page__fieldset']}>
            <legend className={admin['admin-page__legend']}>Tipos de estación</legend>
            <InputText
              label="Tipos de abatidor"
              hint="Ids de MRPeasy, separados por comas."
              value={draft.types.chillTypeIds}
              disabled={isReadOnly}
              onChange={(event) => edit({ types: { ...draft.types, chillTypeIds: event.target.value } })}
            />
            <InputText
              label="Tipos de túnel"
              value={draft.types.tunnelTypeIds}
              disabled={isReadOnly}
              onChange={(event) => edit({ types: { ...draft.types, tunnelTypeIds: event.target.value } })}
            />
            <InputText
              label="Tipos de preparar en frío"
              hint="Dentro del departamento de frío: ✂ Preparar."
              value={draft.types.prepTypeIds}
              disabled={isReadOnly}
              onChange={(event) => edit({ types: { ...draft.types, prepTypeIds: event.target.value } })}
            />
            <InputText
              label="Tipos de etiquetar"
              hint="Dentro del departamento de frío, se ignoran. Cualquier otro tipo de frío es racionar."
              value={draft.types.labelTypeIds}
              disabled={isReadOnly}
              onChange={(event) => edit({ types: { ...draft.types, labelTypeIds: event.target.value } })}
            />
          </fieldset>
        </div>
      </details>

      {problems.length > 0 ? <p role="alert" className={admin['admin-page__error']}>Revisa: {problems.join(', ')}.</p> : null}
      {errorMessage !== null ? <p role="alert" className={admin['admin-page__error']}>{errorMessage}</p> : null}
      {saved && !changed ? <p role="status" className={admin['admin-page__ok']}>Guardado · queda en el historial de configuración.</p> : null}
      <div className={`${admin['admin-page__form-actions']} ${styles.actions}`}>
        <Button type="submit" disabled={!canSave} label={update.isPending ? 'Guardando…' : 'Guardar'} />
      </div>
    </form>
  )
}

/**
 * Administración → Racionado → Alarmas y parámetros: what the two racionado
 * boards read, written as the plant says it, with an example card per board
 * drawn with the values on the form. Saves with `racionadoSettings.update`,
 * which leaves its line in the configuration's history.
 */
export const RacionadoSettingsPage = (): ReactElement => {
  const settingsQuery = trpc.configuration.racionadoSettings.get.useQuery()

  let body: ReactElement
  if (settingsQuery.isPending) {
    body = <Spinner label="Cargando parámetros…" />
  } else if (settingsQuery.isError) {
    body = <ErrorState error={settingsQuery.error} onRetry={() => void settingsQuery.refetch()} />
  } else {
    body = <SettingsForm view={settingsQuery.data} />
  }

  return (
    <section className={admin['admin-page']}>
      <AdminPageHeader
        title="Alarmas y parámetros de racionado"
        intro="Cuándo avisan los tableros de racionado caliente y frío. Cada bloque se puede devolver a sus valores por defecto; lo de abajo, «Avanzado», casi nunca se toca."
      />
      {body}
    </section>
  )
}
